import { useTranslation } from "react-i18next";
import { useSnackbarAlert } from "../context/snackbarAlert";
import { apiUrls } from "../helpers/apiUrls";
import { makeRequest } from "../helpers/makeRequest";
import { getErrorMsg, isEmptyValue, prepareUrlWithQueryParams } from "../helpers/methods";
import { useAxiosPrivate } from "../hooks/useAxiosPrivate";
import useThumbnailService from "./thumbnailService";

export default function useFileService() {
    const axiosPrivate = useAxiosPrivate();
    const snackbarAlert = useSnackbarAlert();

    const { t } = useTranslation();

    const { convertToThumbnail } = useThumbnailService();

    const downloadFile = (fileUrl, fileName, data, searchParams, delegateDownloadFile=false) => {
        fileUrl = prepareUrlWithQueryParams(fileUrl, searchParams);
        let axios;
        if (data) {
            if (delegateDownloadFile) {
                data.file_name = fileName
            }
            axios = axiosPrivate.post(fileUrl, data, { responseType: "blob" });
        } else {
            axios = axiosPrivate.get(fileUrl, { responseType: "blob" });
        }
        axios
            .then((response) => {
                if (delegateDownloadFile) {
                    snackbarAlert.openWarningSnackbarAlert(t("snackbar_alert.file_download_is_delegated_to_celery"))
                    return
                }

                const url = window.URL.createObjectURL(
                    new Blob([response.data], { type: "application/stream" }),
                );
                downloadFileByBlobUrl(url, fileName);
            })
            .catch((error) => {
                error.response.data
                    .text()
                    .then((responseTxt) => {
                        snackbarAlert.openErrorSnackbarAlert(
                            getErrorMsg(
                                JSON.parse(responseTxt),
                                t("snackbar_alert.occurred_error_during_download_file"),
                            ),
                        );
                    })
                    .catch(() => {
                        snackbarAlert.openErrorSnackbarAlert(
                            t("snackbar_alert.occurred_error_during_download_file"),
                        );
                    });
            });
    };

    const dowloadBackendFile = (fileUrl, fileName) => {
        const axios = axiosPrivate.get(fileUrl, { responseType: "blob" });
        axios
            .then((response) => {
                const url = window.URL.createObjectURL(
                    new Blob([response.data], { type: "application/stream" }),
                );
                downloadFileByBlobUrl(url, fileName);
            })
            .catch(() => {
                snackbarAlert.openErrorSnackbarAlert(
                    t("snackbar_alert.occurred_error_during_download_input_file"),
                );
            });
    };

    const downloadFileByBlobUrl = (blobUrl, fileName) => {
        const a = document.createElement("a");
        document.body.appendChild(a);
        a.style = "display: none";
        a.href = blobUrl;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(blobUrl);
    };

    const showFileByBlobUrl = (blobUrl) => {
        const a = document.createElement("a");
        a.href = blobUrl;
        a.target = "_blank";
        a.click();
    };

    const getEnclosuresForItemData = (itemId, searchParams = {}, itemType = "foo") => {
        if (itemId) {
            if (itemType === "fooo") {
                return getEnclosuresForFoos(itemId, searchParams);
            }
            // here other model objects with enclosures
        }
        return Promise.resolve([]);
    };

    const getEnclosuresForFoos = (fooId, searchParams = {}) => {
        return makeRequest(
            axiosPrivate,
            prepareUrlWithQueryParams(`mock-url${fooId}`, searchParams),
        );
    };

    const saveManyAttachments = async (enclosures, commonEnclosureData, searchParams) => {
        const results = await uploadAttachments(enclosures, commonEnclosureData);
        const uploadedData = results.uploadedData;
        const showWaring = results.showWaring;
        // biome-ignore lint/suspicious/noAsyncPromiseExecutor: <legacy>
        return new Promise(async (resolve, reject) => {
            if (uploadedData.length > 0) {
                await createManyEnclosures(uploadedData, searchParams)
                    .then((res) => {
                        if (showWaring) {
                            snackbarAlert.openWarningSnackbarAlert(
                                t("snackbar_alert.occurred_warning_during_upload_file"),
                            );
                        } else {
                            snackbarAlert.openSuccessSnackbarAlert(
                                t("snackbar_alert.enclosure_saved"),
                            );
                            resolve(res);
                        }
                    })
                    .catch((error) => {
                        reject(error);
                    });
            } else {
                snackbarAlert.openErrorSnackbarAlert(
                    t("snackbar_alert.occurred_error_during_upload_file"),
                );
            }
        });
    };

    const uploadAttachments = async (enclosures, commonEnclosureData) => {
        let showWarning = false;
        const getDataToGenerateTokens = enclosures.map((enclosure) => enclosure.token_data);

        const results = await Promise.all([generateSASUploadTokens(getDataToGenerateTokens)]);

        const mergedData = [];
        results[0].forEach((tokenData) => {
            const enclosureData = enclosures.find(
                (obj) => obj.token_data.unique_index === tokenData.unique_index,
            );
            const contentType = enclosureData.token_data.content_type;
            enclosureData.content_type = isEmptyValue(contentType) ? undefined : contentType;

            if (enclosureData) {
                mergedData.push({
                    ...enclosureData,
                    ...tokenData,
                    ...commonEnclosureData,
                });
            }
        });

        const uploadedData = [];
        for (const data of mergedData) {
            data.token_data = undefined;
            try {
                await uploadDataToAzureStorage(data.file, data.upload_url);
                if (data.miniature_upload_url) {
                    convertToThumbnail(data.file, 200, 200, async (thumbnailBlob) => {
                        await uploadDataToAzureStorage(thumbnailBlob, data.miniature_upload_url);
                    });
                }
                uploadedData.push(getDataToSave(data));
            } catch (error) {
                showWarning = true;
            }
        }
        return { uploadedData: uploadedData, showWaring: showWarning };
    };

    const getDataToSave = (enclosure) => {
        //  some data prepare
        return enclosure;
    };

    const createManyEnclosures = async (data, searchParams) => {
        return makeRequest(
            axiosPrivate,
            prepareUrlWithQueryParams(apiUrls.files.massAction, searchParams),
            {
                method: "POST",
                data: data,
            },
        );
    };

    const handleDownloadEnclosure = async (enclosureId, enclosureName) => {
        const data = await getSasTokenToGetFileFromAzure(enclosureId);
        const url = await getBlobFromAzureByUrl(data.download_link, data.content_type);
        downloadFileByBlobUrl(url, enclosureName);
    };

    const handlePreviewEnclosure = async (enclosureId) => {
        const data = await getSasTokenToGetFileFromAzure(enclosureId);
        const url = await getBlobFromAzureByUrl(data.download_link, data.content_type);
        showFileByBlobUrl(url);
    };

    const getSasTokenToGetFileFromAzure = (enclosureId) => {
        return makeRequest(axiosPrivate, apiUrls.enclosureDownloadUrl(enclosureId));
    };

    const getBlobFromAzureByUrl = async (urlToken, contentType) => {
        try {
            const response = await fetch(urlToken);
            const blob = await response.blob();
            const blobWithType = new Blob([blob], { type: contentType });

            return URL.createObjectURL(blobWithType);
        } catch (error) {
            snackbarAlert.openErrorSnackbarAlert(
                t("snackbar_alert.occurred_error_during_upload_file"),
            );
        }
    };

    const generateSASUploadTokens = (enclosuresToUpload) => {
        return makeRequest(axiosPrivate, apiUrls.fileUploader.updateToken, {
            method: "POST",
            data: enclosuresToUpload,
        });
    };

    const uploadDataToAzureStorage = async (file, url) => {
        const formData = new FormData();
        formData.append(file.name, file);

        const myHeaders = new Headers();
        myHeaders.append("x-ms-blob-type", "BlockBlob");
        myHeaders.append("Content-Type", "application/octet-stream");

        return new Promise((resolve, reject) => {
            fetch(url, {
                method: "PUT",
                headers: myHeaders,
                body: file,
            })
                .then((response) => {
                    if (response.ok) {
                        resolve(response);
                    }
                    reject(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    return {
        getEnclosuresForItemData,
        handleDownloadEnclosure,
        handlePreviewEnclosure,
        downloadFileByBlobUrl,
        showFileByBlobUrl,
        uploadAttachments,
        saveManyAttachments,
        downloadFile,
        getBlobFromAzureByUrl,
        generateSASUploadTokens,
        uploadDataToAzureStorage,
        dowloadBackendFile,
    };
}
